import { useState } from "react";

import { POST_CONFIGURATION } from "@/shared/constants/routes";
import { Button, Loader } from "@orgmym/leeloo-core";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Content } from "@/shared/components/content/Content";
import EmptyState from "@/shared/components/empty-state";
import { Error } from "@/shared/components/error";
import { Header } from "@/shared/components/header/Header";
import InfiniteScroll from "@/shared/components/infinite-scroll";
import MediaGridSelection from "@/shared/components/media-grid-selection";
import { Sidebar } from "@/shared/components/sidebar/Sidebar";

import { MAX_MEDIAS_POST } from "@/features/post/domain/constants";
import { Media } from "@/features/post/domain/models/media";
import MymCloudActionBar from "@/features/post/presentation/containers/mym-cloud-action-bar/MymCloudActionBar";
import { useCollectionByIdInfiniteQuery } from "@/features/post/presentation/pages/collection/viewModel";

import * as styles from "./collection.css";
import { useTracking } from "@/shared/hooks/useTracking";

const Collection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setValue } = useFormContext();
  const [selectedMedias, setSelectedMedias] = useState<Media[]>([]);

  const {
    data: collection,
    isLoading,
    isFetchingNextPage,
    refetch,
    isError,
    fetchNextPage,
    hasNextPage
  } = useCollectionByIdInfiniteQuery();

  const { trackEvent } = useTracking();

  const selectItem = (media?: Media) => {
    if (media) {
      setSelectedMedias((prevSelectedMedias) => {
        const isMediaSelected = prevSelectedMedias.some(
          (selectedMedia) => selectedMedia.id === media.id
        );

        if (isMediaSelected) {
          return prevSelectedMedias.filter(
            (selectedMedia) => selectedMedia.id !== media.id
          );
        } else {
          return [media];
        }
      });
    }
  };

  return (
    <Content
      sidebar={<Sidebar withFooter />}
      header={<Header title={collection?.name} withButtonBack />}
    >
      {isLoading ? (
        <Loader
          spin={true}
          data-testid="collection-loader"
          className={styles.loader}
        />
      ) : isError ? (
        <Error onClick={() => refetch()} data-testid="collection-error" />
      ) : !collection || !collection.medias?.length ? (
        <EmptyState
          translations={{
            title: t("mymcloud_recent_empty_state__title"),
            description: t("mymcloud_recent_empty_state__description")
          }}
        />
      ) : (
        <div className={styles.container} data-testid="collection">
          <InfiniteScroll
            hasMore={hasNextPage}
            loadMore={fetchNextPage}
            isLoading={isLoading || isFetchingNextPage}
          >
            <MediaGridSelection
              medias={collection?.medias || []}
              onSelectItem={selectItem}
              selectedMedias={selectedMedias}
              maxItems={MAX_MEDIAS_POST}
            />
          </InfiniteScroll>
          <MymCloudActionBar
            data-testid="collection-action-bar"
            selectedMedias={selectedMedias}
            button={
              <Button
                data-testid="collection-add-media-button"
                onPress={() => {
                  trackEvent("crea_track_media_upload", {
                    source: "mym_cloud",
                    feature: "post"
                  });

                  setValue("media", {
                    id: selectedMedias[0].id,
                    url: selectedMedias[0].preview,
                    file: {
                      type: selectedMedias[0].type
                    }
                  });
                  navigate(`../${POST_CONFIGURATION}`, { replace: true });
                }}
                disabled={selectedMedias.length === 0}
              >
                {t("mymcloud_recent_empty_state_bottombar_button")}
              </Button>
            }
          />
        </div>
      )}
    </Content>
  );
};

export default Collection;
